import { Text } from '@thanx/uikit/text'
import { Role } from 'api/roles'
import { BadgeVariant } from 'constants/roles'
import { useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { generateId } from 'utilities/uniqueId'
import { RoleCard } from './RoleCard'

const ROLE_CARD_WIDTH = 'calc(33.333% - 11px)'
const ROLE_GRID_GAP = 16

type Props = {
  title: string
  tooltip?: string
  roles: Role[]
  defaultExpanded?: boolean
  badgeVariant?: BadgeVariant
  canManage?: boolean
  onDelete: (role: Role) => void
}

export const RoleSection = ({
  title,
  tooltip,
  roles,
  defaultExpanded = true,
  badgeVariant,
  canManage = false,
  onDelete,
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const sectionContentId = useMemo(
    () => `role-section-content-${generateId()}`,
    []
  )
  const tooltipId = useRef(`section-tooltip-${generateId()}`).current

  if (roles.length === 0) return null

  const handleToggle = () => setExpanded(prev => !prev)

  return (
    <div className="mb-l">
      <div className="d-flex align-items-center mb-m">
        <button
          type="button"
          className="d-flex align-items-center cursor-pointer user-select-none border-0 bg-transparent p-0 text-left"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-controls={sectionContentId}
        >
          <i
            className={`fa fa-chevron-${
              expanded ? 'down' : 'right'
            } mr-s grey-70`}
            aria-hidden="true"
          />
          <Text.Header4 bold className="m-0">
            {title}
          </Text.Header4>
          <Text.BodyText4 color="grey70" className="m-0 ml-xs">
            ({roles.length})
          </Text.BodyText4>
        </button>
        {tooltip && (
          <OverlayTrigger
            placement="right"
            trigger={['hover', 'focus']}
            overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
          >
            <button
              type="button"
              className="border-0 bg-transparent p-0 ml-s grey-50 d-inline-flex align-items-center"
              aria-label={tooltip}
              style={{ cursor: 'help' }}
            >
              <i className="fa fa-info-circle" aria-hidden="true" />
            </button>
          </OverlayTrigger>
        )}
      </div>
      {expanded && (
        <div
          id={sectionContentId}
          className="d-flex flex-wrap"
          style={{ gap: ROLE_GRID_GAP }}
        >
          {roles.map(role => (
            <div key={role.id} style={{ width: ROLE_CARD_WIDTH }}>
              <RoleCard
                role={role}
                badgeVariant={badgeVariant}
                canManage={canManage}
                onDelete={onDelete}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
